import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { FiPlus as Plus, FiEdit3 as Edit3, FiSave as Save, FiRotateCcw as RotateCcw, FiStar as Sparkles, FiGlobe as Globe, FiSettings, FiBell as Bell, FiBarChart2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { useProject } from '../../contexts/ProjectContext';
import { useFilter } from '../../contexts/FilterContext';
import WidgetWrapper from './WidgetWrapper';
import WidgetPicker from './WidgetPicker';
import CustomMetricsBuilder from './CustomMetricsBuilder';
import DateRangeFilter from './DateRangeFilter';
import ViewsScreen, { SelectedView } from './ViewsScreen';
import HelpPanel from '../common/HelpPanel';
import {
  getWidgetById,
  getDefaultLayoutForContext,
  getTemplatesForContext
} from './widgetRegistry';
import { fetchWidgetsData, dateRangeToParams } from './widgetDataFetcher';
import { getWidgetTemplateById } from './widgets';
import { createDefaultDateRange, getPresetsForContext, type DateRange } from './dateRangeUtils';
import { dashboardAPI } from '../../services/api';
import { dashboardAPI as realDashboardAPI } from '../../services/realApi';
import MetricWidget from './widgets/MetricWidget';
import ChartWidget from './widgets/ChartWidget';
import ListWidget from './widgets/ListWidget';
import ModuleStatusGrid from './widgets/ModuleStatusGrid';
import ProjectHealthHeatmap from './widgets/ProjectHealthHeatmap';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Custom styles for resize handle
const customStyles = `
  .react-resizable-handle::after {
    content: "";
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 12px;
    height: 12px;
    border-right: 3px solid #7DD3FC !important;
    border-bottom: 3px solid #7DD3FC !important;
    border-radius: 0 0 4px 0;
    opacity: 0.6;
    transition: opacity 0.2s ease;
  }

  .widget-container:hover .react-resizable-handle::after {
    opacity: 1;
  }

  .react-resizable-handle {
    z-index: 10;
  }

  .react-resizable-handle-se {
    cursor: se-resize !important;
    bottom: 0;
    right: 0;
    width: 20px;
    height: 20px;
  }
`;

// Breakpoint configuration
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS: Record<string, number> = { lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 };

interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
}

type Layouts = Record<string, LayoutItem[]>;

interface ViewOption {
  type: 'system' | 'custom';
  id: string | number;
  name: string;
  description?: string;
  isDefault?: boolean;
  isPublished?: boolean;
  template?: Record<string, unknown>;
  dashboard?: Record<string, unknown>;
}

interface CustomDashboard {
  id: string | number;
  name: string;
  description?: string;
  context?: string;
  is_published?: boolean;
  is_default?: boolean;
  layout?: unknown;
}

interface WidgetDefinition {
  id: string;
  name: string;
  type?: string;
  [key: string]: unknown;
}

interface DefaultPreference {
  default_type: string;
  default_key: string;
}

interface WidgetData {
  value?: number | string;
  label?: string;
  trend?: string;
  trendValue?: number | string;
  format?: string;
  color?: string;
  displayValue?: string;
  link?: string;
  chartType?: string;
  data?: unknown;
  dataKey?: string;
  xKey?: string;
  yKey?: string;
  yKeys?: string[];
  colors?: string[];
  stacked?: boolean;
  items?: unknown[];
  maxItems?: number;
  showBadge?: boolean;
  [key: string]: unknown;
}

interface Props {
  title?: string;
  description?: string;
  storageKey?: string;
  context?: string;
  widgetCategory?: string | null;
  customPresets?: Record<string, unknown> | null;
  defaultLayout?: unknown[] | null;
  onLayoutChange?: ((layouts: unknown) => void) | null;
  customHeaderActions?: React.ReactNode | null;
  helpContent?: React.ReactNode | null;
  helpPanelTitle?: string;
  useBackendPersistence?: boolean;
  allowSetDefault?: boolean;
  noPadding?: boolean;
}

/**
 * Generate layouts for all breakpoints from a base layout
 * This ensures widgets are properly positioned for each screen size
 */
const generateLayoutsForAllBreakpoints = (baseLayout: unknown[]): Layouts => {
  if (!baseLayout || baseLayout.length === 0) {
    return { lg: [], md: [], sm: [], xs: [], xxs: [] };
  }

  const layouts: Layouts = {};

  for (const breakpoint of Object.keys(COLS)) {
    const cols = COLS[breakpoint];
    const newLayout: LayoutItem[] = [];
    let currentX = 0;
    let currentY = 0;
    let rowMaxHeight = 0;

    for (const item of baseLayout as LayoutItem[]) {
      // Clamp width to available columns
      const w = Math.min(item.w || 1, cols);
      const h = item.h || 2;

      // If widget doesn't fit in current row, move to next row
      if (currentX + w > cols) {
        currentX = 0;
        currentY += rowMaxHeight;
        rowMaxHeight = 0;
      }

      newLayout.push({
        ...item,
        x: currentX,
        y: currentY,
        w,
        h,
        minW: Math.min(item.minW || 1, cols),
        maxW: cols,
      });

      currentX += w;
      rowMaxHeight = Math.max(rowMaxHeight, h);
    }

    layouts[breakpoint] = newLayout;
  }

  return layouts;
};

/**
 * Extract base widget ID from layout item (handles timestamped IDs like "widget-id-1234567890")
 */
const extractWidgetId = (layoutItemId: string): string => {
  const parts = layoutItemId.split('-');
  return parts.length > 2 && !Number.isNaN(Number(parts[parts.length - 1]))
    ? parts.slice(0, -1).join('-')
    : layoutItemId;
};

const CustomizableDashboard: React.FC<Props> = ({
  title = 'Dashboard',
  description = 'Your personalized operational overview',
  storageKey = 'dashboard-layout',
  context = 'hub',
  widgetCategory = null,
  customPresets = null,
  defaultLayout: propDefaultLayout = null,
  onLayoutChange = null,
  customHeaderActions = null,
  helpContent = null,
  helpPanelTitle = 'Help panel',
  useBackendPersistence = false,
  allowSetDefault = false,
  noPadding = false,
}) => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const navigate = useNavigate();

  // Get effective client ID - use selectedClientId (for partners) or user's client_id (for client users)
  const getEffectiveClientId = useCallback(() => {
    return selectedClientId || user?.client_id;
  }, [selectedClientId, user]);

  const [editMode, setEditMode] = useState(false);
  const [layouts, setLayouts] = useState<Layouts>({ lg: [], md: [], sm: [], xs: [], xxs: [] });
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  const [widgetData, setWidgetData] = useState<Record<string, WidgetData>>({});
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [showMetricsBuilder, setShowMetricsBuilder] = useState(false);
  const [showViewsScreen, setShowViewsScreen] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [customDashboards, setCustomDashboards] = useState<CustomDashboard[]>([]);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  // Editable dashboard name state (for edit mode)
  const [dashboardName, setDashboardName] = useState(title);
  const [dashboardDescription, setDashboardDescription] = useState(description);

  // Sync state with props when they change
  useEffect(() => {
    setDashboardName(title);
    setDashboardDescription(description);
  }, [title, description]);

  // Backend persistence state
  const [dashboardId, setDashboardId] = useState<string | number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isOrgDefault, setIsOrgDefault] = useState(false);
  const initialLoadDone = useRef(false);

  // Track the actual default preference from backend (for dropdown labels)
  const [defaultPreference, setDefaultPreference] = useState<DefaultPreference | null>(null);

  // Get default date range preset based on dashboard context
  const getDefaultPresetForContext = (_dashboardContext: string): string => {
    // All dashboards default to "all_time" to show complete historical data
    return 'all_time';
  };

  // Date range state with localStorage persistence
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const dateRangeKey = `${storageKey}-daterange`;
    const saved = localStorage.getItem(dateRangeKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved date range:', e);
      }
    }
    // Use context-aware default
    const defaultPreset = getDefaultPresetForContext(context);
    return createDefaultDateRange(defaultPreset);
  });

  // Load the actual default preference from backend
  const loadDefaultPreference = useCallback(async () => {
    const clientId = getEffectiveClientId();
    if (!useBackendPersistence || !clientId) return;
    try {
      const result = await dashboardAPI.getDefaultDashboard(clientId, context);
      if (result.success && result.data) {
        setDefaultPreference({
          default_type: result.data.default_type,
          default_key: result.data.default_key,
        });
      } else {
        setDefaultPreference(null);
      }
    } catch {
      setDefaultPreference(null);
    }
  }, [useBackendPersistence, getEffectiveClientId, context]);

  useEffect(() => {
    loadDefaultPreference();
  }, [loadDefaultPreference]);

  // Get templates based on context or custom templates
  const templates = useMemo((): Record<string, Record<string, unknown>> => {
    if (customPresets) return customPresets as Record<string, Record<string, unknown>>;
    return (getTemplatesForContext(context, user) as Record<string, Record<string, unknown>>) || {};
  }, [context, customPresets, user]);

  // Helper to check if a system template is the actual default
  const isActualDefault = useCallback((templateKey: string): boolean => {
    if (defaultPreference) {
      return defaultPreference.default_type === 'system' && defaultPreference.default_key === templateKey;
    }
    // No backend preference - fall back to hardcoded isDefault from registry
    const tmpl = templates?.[templateKey];
    return (tmpl?.isDefault as boolean) || false;
  }, [defaultPreference, templates]);

  // Helper to check if a custom dashboard is the actual default
  const isActualCustomDefault = useCallback((dashId: string | number): boolean => {
    if (!defaultPreference) return false;
    return defaultPreference.default_type === 'custom' && defaultPreference.default_key === String(dashId);
  }, [defaultPreference]);

  // Combined list of all views for dropdown (system + custom)
  const allViewsForDropdown = useMemo((): ViewOption[] => {
    const options: ViewOption[] = [];

    // Add system views (based on templates)
    for (const [key, template] of Object.entries(templates)) {
      options.push({
        type: 'system',
        id: key,
        name: template.name as string,
        description: template.description as string | undefined,
        isDefault: template.isDefault as boolean | undefined,
        template: template,
      });
    }

    // Add custom views (custom dashboards) - filtered by context
    for (const dashboard of customDashboards.filter(d => d.context === context)) {
      options.push({
        type: 'custom',
        id: dashboard.id,
        name: dashboard.name,
        description: dashboard.description,
        isPublished: dashboard.is_published,
        isDefault: dashboard.is_default,
        dashboard: dashboard as unknown as Record<string, unknown>,
      });
    }

    return options;
  }, [templates, customDashboards, context]);

  // Filter custom dashboards by context for dropdown
  const filteredCustomDashboards = useMemo(() => {
    return customDashboards.filter(dashboard => dashboard.context === context);
  }, [customDashboards, context]);

  // Get default layout based on context
  const getContextDefaultLayout = (): unknown[] => {
    // If propDefaultLayout is explicitly provided (even if empty array), use it
    // Only use context default if propDefaultLayout is null or undefined
    if (propDefaultLayout != null) {
      return propDefaultLayout;
    }
    return getDefaultLayoutForContext(context) as unknown[];
  };

  // Detect if we're on iPad/tablet viewport
  useEffect(() => {
    const checkViewport = (): void => {
      const width = window.innerWidth;
      setIsTablet(width >= 1024 && width < 1200);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Load custom dashboards for template dropdown
  useEffect(() => {
    const loadCustomDashboards = async (): Promise<void> => {
      const clientId = getEffectiveClientId();

      if (!clientId) {
        return;
      }

      try {
        const result = await realDashboardAPI.getDashboards(clientId, true);
        if (result.success) {
          setCustomDashboards((result.data || []) as CustomDashboard[]);
        }
      } catch (err) {
        console.error('Failed to load custom dashboards:', err);
      }
    };

    loadCustomDashboards();
  }, [getEffectiveClientId, selectedClientId, user]);

  // Set initial view based on context (default view)
  useEffect(() => {
    if (!selectedViewId && templates) {
      // Find the default view for this context
      const defaultTemplate = Object.entries(templates).find(([, tmpl]) => tmpl.isDefault);
      if (defaultTemplate) {
        const [key] = defaultTemplate;
        setSelectedViewId(`system-${key}`);
      }
    }
  }, [templates, selectedViewId]);

  // Load layout from backend or localStorage
  useEffect(() => {
    // Prevent double-loading
    if (initialLoadDone.current) return;

    const loadLayout = async (): Promise<void> => {
      const clientId = getEffectiveClientId();

      // Try backend first if enabled
      if (useBackendPersistence && clientId) {
        try {
          const result = await dashboardAPI.getDefaultDashboard(clientId, context);
          if (result.success && result.data) {
            const pref = result.data;

            // Handle system template default
            if (pref.default_type === 'system' && pref.default_key) {
              const template = templates[pref.default_key];
              if (template) {
                setSelectedViewId(`system-${pref.default_key}`);
                setIsOrgDefault(true);
                const templateLayout = template.widgets;
                if (templateLayout) {
                  setLayouts(generateLayoutsForAllBreakpoints(templateLayout as unknown[]));
                  initialLoadDone.current = true;
                  return;
                }
              }
            }

            // Handle custom dashboard default
            if (pref.default_type === 'custom' && pref.dashboard) {
              const dashboard = pref.dashboard;
              setDashboardId(dashboard.id);
              setIsOrgDefault(true);
              setSelectedViewId(`custom-${dashboard.id}`);

              const backendLayout = dashboard.layout;
              if (backendLayout) {
                if (Array.isArray(backendLayout)) {
                  setLayouts(generateLayoutsForAllBreakpoints(backendLayout));
                } else if (backendLayout.lg && Array.isArray(backendLayout.lg)) {
                  setLayouts(backendLayout);
                } else {
                  setLayouts(generateLayoutsForAllBreakpoints(getContextDefaultLayout()));
                }
                initialLoadDone.current = true;
                return;
              }
            }
          }
        } catch (error) {
          console.error('Error loading dashboard from backend:', error);
        }
      }

      // Fall back to localStorage
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData) as unknown;
          // Check if saved data is new multi-breakpoint format or old single-layout format
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && (parsed as Record<string, unknown>).lg && Array.isArray((parsed as Record<string, unknown>).lg)) {
            // New format: { lg: [...], md: [...], ... }
            setLayouts(parsed as Layouts);
          } else if (Array.isArray(parsed)) {
            // Old format: single array - migrate to multi-breakpoint
            setLayouts(generateLayoutsForAllBreakpoints(parsed));
          } else {
            setLayouts(generateLayoutsForAllBreakpoints(getContextDefaultLayout()));
          }
        } catch (e) {
          console.error('Error parsing saved layout:', e);
          setLayouts(generateLayoutsForAllBreakpoints(getContextDefaultLayout()));
        }
      } else {
        setLayouts(generateLayoutsForAllBreakpoints(getContextDefaultLayout()));
      }
      initialLoadDone.current = true;
    };

    loadLayout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, context, useBackendPersistence, getEffectiveClientId]);

  // Handle date range change with persistence
  const handleDateRangeChange = useCallback((newDateRange: DateRange): void => {
    setDateRange(newDateRange);
    const dateRangeKey = `${storageKey}-daterange`;
    localStorage.setItem(dateRangeKey, JSON.stringify(newDateRange));
  }, [storageKey]);

  // Get current layout (for the active breakpoint)
  const currentLayout = layouts[currentBreakpoint] || layouts.lg || [];

  // Load widget data - now uses batch fetching via widgetDataFetcher
  useEffect(() => {
    const loadWidgetData = async (): Promise<void> => {
      // Use lg layout as the source of truth for widget IDs
      const layoutForData = layouts.lg || [];
      if (!user || layoutForData.length === 0) return;

      setIsLoadingData(true);

      // Collect widget templates for batch fetching
      const widgetsToFetch: unknown[] = [];
      const layoutItemMap: Record<string, string> = {}; // Maps widgetId -> layoutItem.i (for timestamped IDs)

      for (const item of layoutForData) {
        const widgetId = extractWidgetId(item.i);
        const template = getWidgetTemplateById(widgetId);
        if (template) {
          widgetsToFetch.push(template);
          layoutItemMap[widgetId] = item.i;
        }
      }

      // Batch fetch all widgets - groups by dataSource for efficiency
      const params = dateRangeToParams(dateRange) as Record<string, unknown>;
      const effectiveClientId = getEffectiveClientId();

      const { results } = await fetchWidgetsData(widgetsToFetch as Parameters<typeof fetchWidgetsData>[0], {
        user,
        clientId: effectiveClientId,
        dateRange,
        params,
      }) as { results: Record<string, { data?: WidgetData }> };

      // Map results back to layout items
      const data: Record<string, WidgetData> = {};
      for (const widget of widgetsToFetch as Array<{ id: string }>) {
        const layoutKey = layoutItemMap[widget.id];
        const result = results[widget.id];
        data[layoutKey] = result?.data || {
          value: 0,
          label: 'No Data',
          format: 'number',
          color: 'gray',
        };
      }

      setWidgetData(data);
      setIsLoadingData(false);
    };

    loadWidgetData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layouts.lg, user, dateRange, selectedProjectId, filterType, filterId]);

  // Handle breakpoint change
  const handleBreakpointChange = (newBreakpoint: string): void => {
    setCurrentBreakpoint(newBreakpoint);
  };

  // Handle layout change from react-grid-layout
  const handleLayoutChange = (_currentLayout: LayoutItem[], allLayouts: Layouts): void => {
    setLayouts(allLayouts);
    if (onLayoutChange) {
      onLayoutChange(allLayouts);
    }
  };

  const handleSaveLayout = async (): Promise<void> => {
    const clientId = getEffectiveClientId();

    // Always save to localStorage as cache
    localStorage.setItem(storageKey, JSON.stringify(layouts));

    // Save to backend if enabled
    if (useBackendPersistence && clientId) {
      setIsSaving(true);
      try {
        if (dashboardId) {
          // Update existing custom dashboard - use editable name and description
          await dashboardAPI.updateDashboard(String(dashboardId), {
            name: dashboardName.trim() || 'Untitled Dashboard',
            description: dashboardDescription.trim() || null,
            layout: layouts,
          });
        } else {
          // Create new dashboard - auto-generate name from current view
          let newDashboardName: string;

          // Get base name from current view
          if (selectedViewId?.startsWith('system-')) {
            const templateKey = selectedViewId.replace('system-', '');
            const template = templates[templateKey];
            const baseName = template ? (template.name as string) : title;

            // Check for existing custom versions
            const existingCustom = customDashboards.filter(d =>
              d.name.startsWith(`${baseName} (Custom`)
            );

            if (existingCustom.length === 0) {
              newDashboardName = `${baseName} (Custom)`;
            } else {
              // Find the highest number
              const numbers = existingCustom.map(d => {
                const match = d.name.match(/\(Custom (\d+)\)/);
                return match ? Number.parseInt(match[1]) : 1;
              });
              const maxNum = Math.max(...numbers, 1);
              newDashboardName = `${baseName} (Custom ${maxNum + 1})`;
            }
          } else if (selectedViewId?.startsWith('custom-')) {
            // Saving from a custom dashboard - increment its name
            const sourceDashboard = customDashboards.find(d => `custom-${d.id}` === selectedViewId);
            const baseName = sourceDashboard?.name ?? title;
            newDashboardName = `${baseName} (Copy)`;
          } else {
            // Fallback
            newDashboardName = `${title} (Custom)`;
          }

          const result = await dashboardAPI.createDashboard({
            name: newDashboardName,
            description: dashboardDescription.trim() || null,
            context: context,
            layout: layouts,
            is_published: false,
          }, clientId);

          if (result.success && result.data) {
            const newDashboard = result.data as Record<string, unknown>;
            setDashboardId(newDashboard.id as string | number);
            setSelectedViewId(`custom-${String(newDashboard.id)}`);
            // Update local state with the new dashboard name
            setDashboardName(newDashboardName);
            // Reload custom dashboards to show the new one
            const reloadResult = await realDashboardAPI.getDashboards(clientId, true);
            if (reloadResult.success) {
              setCustomDashboards((reloadResult.data || []) as CustomDashboard[]);
            }
          }
        }
      } catch (error) {
        console.error('Error saving dashboard to backend:', error);
      } finally {
        setIsSaving(false);
      }
    }

    setEditMode(false);
  };

  // Handle setting dashboard as organization default
  const handleSetAsDefault = async (): Promise<void> => {
    const clientId = getEffectiveClientId();
    if (!dashboardId || !clientId) return;

    setIsSettingDefault(true);
    try {
      const result = await dashboardAPI.setDefaultPreference('custom', String(dashboardId), context, clientId);
      if (result.success) {
        setIsOrgDefault(true);
        loadDefaultPreference();
      }
    } catch (error) {
      console.error('Error setting dashboard as default:', error);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleResetLayout = (): void => {
    const defaultLayout = getContextDefaultLayout();
    const newLayouts = generateLayoutsForAllBreakpoints(defaultLayout);
    setLayouts(newLayouts);
    localStorage.removeItem(storageKey);
  };

  const handleAddWidget = (widgetId: string): void => {
    const widget = getWidgetById(widgetId) as WidgetDefinition | null;
    if (!widget) {
      console.error('Widget not found:', widgetId);
      return;
    }

    const newItemId = `${widgetId}-${Date.now()}`;
    const newLayouts = { ...layouts };

    // Add widget to each breakpoint with appropriate positioning
    for (const breakpoint of Object.keys(COLS)) {
      const currentBpLayout = layouts[breakpoint] || [];
      const cols = COLS[breakpoint];

      // Find the next available position for this breakpoint
      const maxY = currentBpLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

      const defaultSize = (widget as { defaultSize?: Record<string, number> }).defaultSize;

      const newItem: LayoutItem = {
        i: newItemId,
        x: 0,
        y: maxY,
        w: Math.min(defaultSize?.w || 1, cols),
        h: defaultSize?.h || 2,
        minW: Math.min(defaultSize?.minW || 1, cols),
        minH: defaultSize?.minH || 1,
      };

      newLayouts[breakpoint] = [...currentBpLayout, newItem];
    }

    setLayouts(newLayouts);
    setShowWidgetPicker(false);
  };

  const handleRemoveWidget = (widgetId: string): void => {
    // Remove widget from all breakpoints
    const newLayouts: Layouts = {};
    for (const breakpoint of Object.keys(layouts)) {
      newLayouts[breakpoint] = (layouts[breakpoint] || []).filter(item => item.i !== widgetId);
    }
    setLayouts(newLayouts);
  };

  const handleRefreshWidget = async (widgetId: string): Promise<void> => {
    const baseWidgetId = extractWidgetId(widgetId);
    const template = getWidgetTemplateById(baseWidgetId);

    if (template && user) {
      try {
        const params = dateRangeToParams(dateRange) as Record<string, unknown>;
        const { results } = await fetchWidgetsData([template] as Parameters<typeof fetchWidgetsData>[0], {
          user,
          dateRange,
          params,
        }) as { results: Record<string, { data?: WidgetData }> };
        const result = results[(template as Record<string, unknown>).id as string];
        setWidgetData(prev => ({
          ...prev,
          [widgetId]: result?.data || { value: 0, label: 'No Data', format: 'number', color: 'gray' },
        }));
      } catch (error) {
        console.error(`Error refreshing widget ${baseWidgetId}:`, error);
      }
    }
  };

  // Handler for selecting a view from the ViewsScreen
  // SelectedView from ViewsScreen: { type: 'system'|'custom'; view: SystemView|CustomDashboard; key: string|number }
  const handleSelectView = async (viewData: { type: string; key: string | number; view: { name?: string; description?: string; widgets?: unknown[]; id?: string | number; layout?: unknown; [key: string]: unknown } }): Promise<void> => {
    if (viewData.type === 'system') {
      // Apply system view directly
      const template = viewData.view;
      if (template?.widgets) {
        const newLayouts = generateLayoutsForAllBreakpoints(template.widgets);
        setLayouts(newLayouts);
        localStorage.setItem(storageKey, JSON.stringify(newLayouts));
        setSelectedViewId(`system-${String(viewData.key)}`);
        // Reset dashboard ID - we're starting from a system template
        setDashboardId(null);
        setIsOrgDefault(false);
        // Update editable name/description from template
        setDashboardName(template.name as string);
        setDashboardDescription((template.description as string) || '');
      }
    } else if (viewData.type === 'custom') {
      // Fetch full dashboard details from API to get complete layout
      try {
        const viewDashboardId = String(viewData.view.id ?? viewData.key);
        const result = await realDashboardAPI.getDashboard(viewDashboardId);
        if (result.success && result.data) {
          const dashboard = result.data;
          const layoutData = dashboard.layout || {};
          const layoutArray = Array.isArray(layoutData)
            ? layoutData
            : (layoutData.lg ?? layoutData.default ?? []);

          const newLayouts = generateLayoutsForAllBreakpoints(layoutArray);
          setLayouts(newLayouts);
          localStorage.setItem(storageKey, JSON.stringify(newLayouts));
          setSelectedViewId(`custom-${String(dashboard.id)}`);
          // Set dashboard ID - we're loading a custom dashboard
          setDashboardId(dashboard.id);
          setIsOrgDefault(dashboard.is_default || false);
          // Update editable name/description from custom dashboard
          setDashboardName(dashboard.name);
          setDashboardDescription(dashboard.description || '');
        }
      } catch (err) {
        console.error('Error loading custom dashboard:', err);
      }
    }
  };

  // Handler for quick view selection from dropdown
  const handleQuickViewSelect = async (viewOption: ViewOption): Promise<void> => {
    if (!viewOption) return;

    const viewId = `${viewOption.type}-${viewOption.id}`;

    if (viewOption.type === 'system') {
      // Apply system view directly
      const template = viewOption.template;
      if (template?.widgets) {
        const newLayouts = generateLayoutsForAllBreakpoints(template.widgets as unknown[]);
        setLayouts(newLayouts);
        localStorage.setItem(storageKey, JSON.stringify(newLayouts));
        setSelectedViewId(viewId);
        // Reset dashboard ID - we're starting from a system template
        setDashboardId(null);
        setIsOrgDefault(false);
        // Update editable name/description from template
        setDashboardName(template.name as string);
        setDashboardDescription((template.description as string) || '');
      }
    } else if (viewOption.type === 'custom') {
      // Fetch full dashboard details from API to get complete layout
      try {
        const result = await realDashboardAPI.getDashboard(String(viewOption.id));
        if (result.success && result.data) {
          const dashboard = result.data;
          const layoutData = dashboard.layout || {};
          const layoutArray = Array.isArray(layoutData)
            ? layoutData
            : (layoutData.lg ?? layoutData.default ?? []);

          const newLayouts = generateLayoutsForAllBreakpoints(layoutArray);
          setLayouts(newLayouts);
          localStorage.setItem(storageKey, JSON.stringify(newLayouts));
          setSelectedViewId(viewId);
          // Set dashboard ID - we're loading a custom dashboard
          setDashboardId(dashboard.id);
          setIsOrgDefault(dashboard.is_default || false);
          // Update editable name/description from custom dashboard
          setDashboardName(dashboard.name);
          setDashboardDescription(dashboard.description || '');
        }
      } catch (err) {
        console.error('Error loading custom dashboard:', err);
      }
    }
  };

  const handleSaveCustomMetric = (_metricConfig: unknown): void => {
    const newItemId = `custom-${Date.now()}`;
    const newLayouts = { ...layouts };

    // Add custom metric widget to each breakpoint
    for (const breakpoint of Object.keys(COLS)) {
      const currentBpLayout = layouts[breakpoint] || [];
      const maxY = currentBpLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

      const newWidget: LayoutItem = {
        i: newItemId,
        x: 0,
        y: maxY,
        w: 1,
        h: 1,
        minW: 1,
        minH: 1,
      };

      newLayouts[breakpoint] = [...currentBpLayout, newWidget];
    }

    setLayouts(newLayouts);

    // Store custom metric configuration
    const customMetrics = JSON.parse(localStorage.getItem('custom-metrics') || '{}') as Record<string, unknown>;
    customMetrics[newItemId] = _metricConfig;
    localStorage.setItem('custom-metrics', JSON.stringify(customMetrics));

    setShowMetricsBuilder(false);
  };

  const renderWidget = (layoutItem: LayoutItem): React.ReactElement => {
    // Extract widget ID - handle both "widget-id" and "widget-id-timestamp" formats
    const parts = layoutItem.i.split('-');
    const widgetId = parts.length > 2 && !Number.isNaN(Number(parts[parts.length - 1]))
      ? parts.slice(0, -1).join('-')
      : layoutItem.i;

    const widget = getWidgetById(widgetId) as WidgetDefinition | null;
    const data = widgetData[layoutItem.i];

    if (!widget) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500 dark:text-gray-400">Widget not found</div>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    let content: React.ReactElement;
    switch (widget.type) {
      case 'metric': {
        content = (
          <MetricWidget
            value={(data.value as number | string) ?? 0}
            label={data.label ?? ''}
            trend={data.trend}
            trendValue={data.trendValue}
            format={data.format}
            color={data.color}
            displayValue={data.displayValue}
            link={data.link}
          />
        );
        break;
      }
      case 'chart': {
        content = (
          <div
            className={`h-full w-full text-gray-500 dark:text-gray-400 ${data.link ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={data.link ? () => navigate(data.link!) : undefined}
            onKeyDown={data.link ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(data.link!); } } : undefined}
            role={data.link ? 'button' : undefined}
            tabIndex={data.link ? 0 : undefined}
          >
            <ChartWidget
              type={data.chartType as string | undefined}
              data={(data.data as Record<string, unknown>[]) || []}
              dataKey={data.dataKey}
              xKey={data.xKey}
              yKey={data.yKey}
              yKeys={data.yKeys}
              color={data.color}
              colors={data.colors}
              format={data.format}
              stacked={data.stacked}
            />
          </div>
        );
        break;
      }
      case 'list': {
        content = (
          <div
            className={`h-full w-full text-gray-500 dark:text-gray-400 ${data.link ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={data.link ? () => navigate(data.link!) : undefined}
            onKeyDown={data.link ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(data.link!); } } : undefined}
            role={data.link ? 'button' : undefined}
            tabIndex={data.link ? 0 : undefined}
          >
            <ListWidget
              items={(data.items as { title: string; [key: string]: unknown }[]) || []}
              maxItems={data.maxItems}
              showBadge={data.showBadge}
            />
          </div>
        );
        break;
      }
      case 'custom': {
        if (widgetId === 'module-status-grid') {
          content = <ModuleStatusGrid data={data as Record<string, unknown>} />;
        } else if (widgetId === 'project-health-heatmap') {
          content = <ProjectHealthHeatmap data={data as Record<string, unknown>} />;
        } else {
          content = <div className="text-gray-500">Custom widget not implemented</div>;
        }
        break;
      }
      default: {
        content = <div className="text-gray-500">Widget type not implemented</div>;
      }
    }

    return (
      <WidgetWrapper
        widget={widget}
        editMode={editMode}
        onRemove={() => handleRemoveWidget(layoutItem.i)}
        onRefresh={() => handleRefreshWidget(layoutItem.i)}
      >
        {content}
      </WidgetWrapper>
    );
  };

  let dashboardHeaderContent: React.ReactNode;
  if (editMode && dashboardId) {
    // Editing a custom dashboard - name is editable
    dashboardHeaderContent = (
      <>
        <input
          type="text"
          value={dashboardName}
          onChange={(e) => setDashboardName(e.target.value)}
          className="text-3xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-blue-500 focus:outline-none focus:border-blue-600 transition-colors w-full max-w-2xl"
          placeholder="Dashboard Name"
        />
        <textarea
          value={dashboardDescription}
          onChange={(e) => setDashboardDescription(e.target.value)}
          rows={1}
          className="text-sm text-gray-600 dark:text-gray-400 mt-2 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 transition-colors w-full max-w-2xl resize-none"
          placeholder="Dashboard description..."
        />
        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
          Drag widgets to rearrange, resize from corners, click + to add more
        </p>
      </>
    );
  } else if (editMode && !dashboardId) {
    // Editing a system template - name is NOT editable (will auto-generate on save)
    dashboardHeaderContent = (
      <>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          {dashboardName}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">
          Customizing view • Will create a new custom dashboard when saved
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
          Drag widgets to rearrange, resize from corners, click + to add more
        </p>
      </>
    );
  } else {
    // View mode - show name and description
    dashboardHeaderContent = (
      <>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          {dashboardName}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">
          {dashboardDescription}{isLoadingData ? ' • Loading...' : ''}
        </p>
      </>
    );
  }

  return (
    <div className={noPadding ? "" : "px-4 sm:px-6 lg:px-8 py-8"}>
      <style>{customStyles}</style>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            {dashboardHeaderContent}
          </div>

          <div className="flex items-center gap-3">
            {/* Date Range Filter - Always visible when not in edit mode */}
            {!editMode && (
              <DateRangeFilter
                value={dateRange}
                onChange={handleDateRangeChange}
                compact={true}
                disabled={isLoadingData}
                presets={getPresetsForContext(context)}
              />
            )}

            {customHeaderActions && !editMode && (
              <>{customHeaderActions}</>
            )}
            {!editMode && (
              <>
                {!customHeaderActions && (
                  <>
                    {/* Quick View Selector Dropdown */}
                    <select
                      value={selectedViewId}
                      onChange={(e) => {
                        const selectedOption = allViewsForDropdown.find(
                          opt => `${opt.type}-${opt.id}` === e.target.value
                        );
                        if (selectedOption) {
                          handleQuickViewSelect(selectedOption);
                        }
                      }}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    >
                      {Object.entries(templates).length > 0 && (
                        <optgroup label="System Templates">
                          {Object.entries(templates).map(([key, template]) => (
                            <option key={`system-${key}`} value={`system-${key}`}>
                              {template.name as string}{isActualDefault(key) ? ' (Default)' : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {filteredCustomDashboards.length > 0 && (
                        <optgroup label="Custom Views">
                          {filteredCustomDashboards.map((dashboard) => (
                            <option key={`custom-${dashboard.id}`} value={`custom-${dashboard.id}`}>
                              {dashboard.name}
                              {isActualCustomDefault(dashboard.id) ? ' (Default)' : ''}
                              {dashboard.is_published ? ' (Published)' : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>

                    {/* Manage Views Button */}
                    <button
                      onClick={() => setShowViewsScreen(true)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title="Manage Views"
                    >
                      <FiSettings className="w-5 h-5" />
                    </button>
                  </>
                )}
                {!customHeaderActions && (
                  <>
                    {/* Configure Notifications button - links to Settings */}
                    <button
                      onClick={() => navigate('/settings/notifications')}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title="Configure Notifications"
                    >
                      <Bell className="w-4 h-4" />
                      <span className="hidden sm:inline">Notifications</span>
                    </button>
                    <button
                      onClick={() => setEditMode(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Customize</span>
                    </button>
                  </>
                )}
              </>
            )}
            {editMode && (
              <>
                <button
                  onClick={() => setShowWidgetPicker(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Widget</span>
                </button>
                <button
                  onClick={() => setShowMetricsBuilder(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Custom Metric</span>
                </button>
                <button
                  onClick={handleResetLayout}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Reset</span>
                </button>
                <button
                  onClick={handleSaveLayout}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{isSaving ? 'Saving...' : 'Save Layout'}</span>
                </button>
                {/* Set as Default button - only shown for admins with backend persistence */}
                {allowSetDefault && useBackendPersistence && dashboardId && !isOrgDefault && (
                  <button
                    onClick={handleSetAsDefault}
                    disabled={isSettingDefault}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSettingDefault ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Globe className="w-4 h-4" />
                    )}
                    <span>{isSettingDefault ? 'Setting...' : 'Set as Default'}</span>
                  </button>
                )}
                {/* Show "Organization Default" badge if this is the default */}
                {isOrgDefault && (
                  <span className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md text-sm">
                    <Globe className="w-4 h-4" />
                    <span>Organization Default</span>
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="pb-8">
        {(!initialLoadDone.current || isLoadingData) && (
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}
        {initialLoadDone.current && !isLoadingData && currentLayout.length === 0 && (
          <div className="text-center py-12">
            <FiBarChart2 className="w-10 h-10 mb-4 mx-auto text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No widgets configured. Click "Customize" to add widgets to your dashboard.
            </p>
            {editMode && (
              <button
                onClick={() => setShowWidgetPicker(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add Your First Widget
              </button>
            )}
          </div>
        )}
        {initialLoadDone.current && !isLoadingData && currentLayout.length > 0 && (
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            rowHeight={isTablet ? 160 : 200}
            isDraggable={editMode}
            isResizable={editMode}
            onLayoutChange={handleLayoutChange}
            onBreakpointChange={handleBreakpointChange}
            draggableHandle=".cursor-move"
            margin={isTablet ? [12, 12] : [20, 20]}
            compactType={null}
            preventCollision={false}
          >
            {currentLayout.map((item) => (
              <div key={item.i} className="widget-container">
                {renderWidget(item)}
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </div>

      {/* Widget Picker Modal */}
      {showWidgetPicker && (
        <WidgetPicker
          onSelect={handleAddWidget}
          onClose={() => setShowWidgetPicker(false)}
          defaultCategory={widgetCategory}
        />
      )}

      {/* Custom Metrics Builder Modal */}
      {showMetricsBuilder && (
        <CustomMetricsBuilder
          onSave={handleSaveCustomMetric}
          onClose={() => setShowMetricsBuilder(false)}
        />
      )}

      {/* Views Screen Modal */}
      {showViewsScreen && (
        <ViewsScreen
          context={context}
          onClose={() => setShowViewsScreen(false)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSelectView={handleSelectView as unknown as (view: SelectedView) => void}
          onDefaultChanged={loadDefaultPreference}
        />
      )}

      {/* Help Panel */}
      {helpContent && (
        <HelpPanel
          isOpen={showHelpPanel}
          onClose={() => setShowHelpPanel(false)}
          title={helpPanelTitle}
        >
          {helpContent}
        </HelpPanel>
      )}
    </div>
  );
};

export default CustomizableDashboard;
